import { useCallback, useEffect, useState } from 'react';
import { IS_BROWSER } from '@utils/constants/app-constants.ts';

export const SIDEBAR_COLLAPSED_STORAGE_KEY =
  'scriptix_editor_sidebar_collapsed';
export const DEFAULT_SIDEBAR_COLLAPSED = true;

/**
 * Parse a persisted sidebar-collapsed value. Only the literal strings
 * `'true'` / `'false'` are honoured; anything else (missing key, empty
 * string, JSON blob, legacy typo) falls back to the collapsed default so
 * the editor rail never boots into an unexpected state.
 */
export function parseSidebarCollapsed(raw: string | null): boolean {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return DEFAULT_SIDEBAR_COLLAPSED;
}

/** Read the current persisted state from a Storage-like object. */
export function readSidebarCollapsed(
  storage: Pick<Storage, 'getItem'> | null | undefined
): boolean {
  if (!storage) return DEFAULT_SIDEBAR_COLLAPSED;
  try {
    return parseSidebarCollapsed(
      storage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY)
    );
  } catch {
    return DEFAULT_SIDEBAR_COLLAPSED;
  }
}

/** Write the new state to a Storage-like object. Errors are swallowed. */
export function writeSidebarCollapsed(
  storage: Pick<Storage, 'setItem'> | null | undefined,
  next: boolean
): void {
  if (!storage) return;
  try {
    storage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
  } catch {
    /* noop */
  }
}

/**
 * Every mounted instance of `useSidebarCollapsed`, so a toggle in one
 * component (e.g. the sidebar rail) immediately reaches the others
 * (e.g. the transcript editor's waveform strip that offsets by rail
 * width). The browser's `storage` event only fires in *other* tabs,
 * so same-tab subscribers never heard the change without this.
 */
const sameTabListeners = new Set<(next: boolean) => void>();

const notifySameTabListeners = (next: boolean) => {
  sameTabListeners.forEach(listener => listener(next));
};

/**
 * Editor sidebar rail state (#2230). Persisted per browser and shared
 * across the transcript and caption editors via a single localStorage
 * key, same-tab subscriber notifications, and cross-tab `storage` sync.
 */
export function useSidebarCollapsed(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readSidebarCollapsed(IS_BROWSER ? window.localStorage : null)
  );

  // Every instance registers its own setter so a toggle anywhere in the
  // tab fans out to all of them before the next frame.
  useEffect(() => {
    sameTabListeners.add(setCollapsed);
    return () => {
      sameTabListeners.delete(setCollapsed);
    };
  }, []);

  const set = useCallback((next: boolean) => {
    notifySameTabListeners(next);
    if (IS_BROWSER) writeSidebarCollapsed(window.localStorage, next);
  }, []);

  useEffect(() => {
    if (!IS_BROWSER) return;
    const handler = (e: StorageEvent) => {
      if (e.key !== SIDEBAR_COLLAPSED_STORAGE_KEY) return;
      if (e.newValue === 'true') notifySameTabListeners(true);
      else if (e.newValue === 'false') notifySameTabListeners(false);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return [collapsed, set];
}
